import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  ChevronRight,
  ChevronLeft,
  Search,
  Download,
  RefreshCw,
  Users,
  User,
  Inbox,
  MapPin,
  Building,
  CheckSquare,
  Square,
  ArrowUpDown,
  SlidersHorizontal,
  X,
  DollarSign,
  Car,
  FileText,
  ClipboardList,
  AlertOctagon,
  Eye,
  Clock
} from 'lucide-react';
import Footer from '../common/Footer';
import PriorityBadge from '../common/PriorityBadge';
import RiskScore from '../common/RiskScore';
import SLAIndicator from '../common/SLAIndicator';
import AuditTypeIcon from '../common/AuditTypeIcon';
import { useTheme } from '../../context/ThemeContext';
import { getThemeClasses } from '../../utils/theme';
import { usePayments } from '../../hooks/usePayments';
import { useAuth } from '../../hooks/useAuth';

// ============================================
// QUEUE VIEW BUTTON (Queue-specific component)
// ============================================

const QueueViewButton = ({ label, value, subValue, icon: Icon, color = 'blue', onClick, active }) => {
  const colors = {
    blue: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    amber: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    red: 'bg-red-500/20 text-red-400 border-red-500/30',
    green: 'bg-green-500/20 text-green-400 border-green-500/30',
    purple: 'bg-purple-500/20 text-purple-400 border-purple-500/30'
  };

  return (
    <button
      onClick={onClick}
      className={`flex-1 p-4 rounded-xl border-2 transition text-left ${
        active ? colors[color] : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
      }`}
    >
      <div className="flex items-center justify-between">
        <div>
          <p className={`text-2xl font-bold ${active ? '' : 'text-slate-800 dark:text-slate-100'}`}>{value}</p>
          <p className={`text-sm ${active ? '' : 'text-slate-500 dark:text-slate-400'}`}>{label}</p>
          {subValue && <p className="text-xs text-slate-400 mt-1">{subValue}</p>}
        </div>
        <Icon className={`w-8 h-8 ${active ? '' : 'text-slate-300 dark:text-slate-600'}`} />
      </div>
    </button>
  );
};

// ============================================
// CASE QUEUE PAGE
// ============================================

const CaseQueuePage = () => {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const theme = getThemeClasses(isDarkMode);
  const { user } = useAuth();
  const { payments, queueStats, unresolvedItems, loading } = usePayments();
  const [activeAuditType, setActiveAuditType] = useState('All');
  const [queueView, setQueueView] = useState('team'); // 'my', 'team', 'unassigned', 'dueToday'
  const [selectedItems, setSelectedItems] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [sortBy, setSortBy] = useState('sla');
  const [sortOrder, setSortOrder] = useState('asc');
  const [searchQuery, setSearchQuery] = useState('');

  // Filter states
  const [filters, setFilters] = useState({
    region: '',
    vendor: '',
    riskScoreMin: '',
    riskScoreMax: '',
    dateFrom: '',
    dateTo: ''
  });

  const auditTypes = [
    { id: 'All', label: 'All Types', count: 47, icon: Eye },
    { id: 'Financial', label: 'Financial', count: 18, icon: DollarSign },
    { id: 'Mileage', label: 'Mileage', count: 15, icon: Car },
    { id: 'NCCI Edits', label: 'NCCI Edits', count: 8, icon: FileText },
    { id: 'SMOT', label: 'SMOT', count: 4, icon: ClipboardList },
    { id: 'Unresolved', label: 'Unresolved', count: 2, icon: AlertOctagon }
  ];

  const regions = ['Region 1', 'Region 2', 'Region 3', 'Region 4', 'Region 5', 'Region 6'];
  const vendors = ['Vendor A', 'Vendor B', 'Vendor C', 'Vendor D'];

  // Filter items based on selections
  const filteredItems = payments.filter((item) => {
    // Audit type filter
    if (activeAuditType !== 'All' && item.auditType !== activeAuditType) return false;

    // Queue view filter
    if (queueView === 'my' && item.assignee !== user?.name) return false;
    if (queueView === 'unassigned' && item.assignee !== null) return false;
    if (queueView === 'dueToday' && item.slaHours > 24) return false;

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesId = item.id.toLowerCase().includes(query);
      const matchesProvider = item.provider.toLowerCase().includes(query);
      const matchesNpi = item.npi?.toLowerCase().includes(query);

      if (!matchesId && !matchesProvider && !matchesNpi) return false;
    }

    // Advanced filters
    if (filters.region && item.region !== filters.region) return false;
    if (filters.vendor && item.vendor !== filters.vendor) return false;
    if (filters.riskScoreMin && item.riskScore < parseInt(filters.riskScoreMin)) return false;
    if (filters.riskScoreMax && item.riskScore > parseInt(filters.riskScoreMax)) return false;

    // Date filters
    if (filters.dateFrom && item.serviceDate) {
      const itemDate = new Date(item.serviceDate);
      const fromDate = new Date(filters.dateFrom);
      if (itemDate < fromDate) return false;
    }
    if (filters.dateTo && item.serviceDate) {
      const itemDate = new Date(item.serviceDate);
      const toDate = new Date(filters.dateTo);
      if (itemDate > toDate) return false;
    }

    return true;
  });

  // Sort items
  const sortedItems = [...filteredItems].sort((a, b) => {
    let comparison = 0;
    switch (sortBy) {
      case 'sla':
        comparison = a.slaHours - b.slaHours;
        break;
      case 'risk':
        comparison = b.riskScore - a.riskScore;
        break;
      case 'amount':
        comparison = b.amount - a.amount;
        break;
      case 'date':
        comparison = new Date(b.serviceDate) - new Date(a.serviceDate);
        break;
      default:
        comparison = 0;
    }
    return sortOrder === 'asc' ? comparison : -comparison;
  });

  const toggleSelectItem = (id) => {
    setSelectedItems((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedItems.length === sortedItems.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(sortedItems.map((item) => item.id));
    }
  };

  const clearFilters = () => {
    setFilters({
      region: '',
      vendor: '',
      riskScoreMin: '',
      riskScoreMax: '',
      dateFrom: '',
      dateTo: ''
    });
    setSearchQuery('');
  };

  const hasActiveFilters = Object.values(filters).some((v) => v !== '') || searchQuery !== '';

  // Show loading state while data is being fetched
  if (loading) {
    return (
      <div className={`min-h-screen ${theme.bgPrimary} flex flex-col`}>
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 dark:border-blue-400 mb-4"></div>
            <p className={theme.textTertiary}>Loading case queue...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${theme.bgPrimary} flex flex-col`}>
      <div className="flex-1">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* Page Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className={`text-2xl font-bold ${theme.textPrimary}`}>Case Queue</h2>
              <p className={`${theme.textTertiary} mt-1`}>Manage and process audit cases</p>
            </div>
            <div className="flex items-center gap-3">
              <button className={`inline-flex items-center gap-2 px-4 py-2 text-sm ${theme.textSecondary} ${theme.bgHover} rounded-lg transition`}>
                <RefreshCw className="w-4 h-4" />
                Refresh
              </button>
              <button className={`inline-flex items-center gap-2 px-4 py-2 text-sm ${theme.textSecondary} ${theme.bgHover} rounded-lg transition`}>
                <Download className="w-4 h-4" />
                Export
              </button>
            </div>
          </div>

          {/* Queue Stats */}
          <div className="flex gap-4 mb-6">
            <QueueViewButton
              label="My Queue"
              value={queueStats.myQueue}
              icon={User}
              color="blue"
              onClick={() => setQueueView('my')}
              active={queueView === 'my'}
            />
            <QueueViewButton
              label="Team Queue"
              value={queueStats.teamQueue}
              subValue={`${queueStats.overdueCount} overdue`}
              icon={Users}
              color="amber"
              onClick={() => setQueueView('team')}
              active={queueView === 'team'}
            />
            <QueueViewButton
              label="Unassigned"
              value={queueStats.unassigned}
              subValue="Needs attention"
              icon={Inbox}
              color="red"
              onClick={() => setQueueView('unassigned')}
              active={queueView === 'unassigned'}
            />
            <QueueViewButton
              label="Due Today"
              value={queueStats.dueTodayCount}
              subValue={`Avg ${queueStats.avgProcessingTime}h processing`}
              icon={Clock}
              color="purple"
              onClick={() => setQueueView('dueToday')}
              active={queueView === 'dueToday'}
            />
          </div>

          {/* Audit Type Tabs */}
          <div className={`${theme.bgSecondary} rounded-xl border ${theme.border} shadow-sm overflow-hidden`}>
            <div className={`border-b ${theme.border}`}>
              <div className="flex">
                {auditTypes.map((type) => (
                  <button
                    key={type.id}
                    onClick={() => setActiveAuditType(type.id)}
                    className={`flex items-center gap-2 px-5 py-4 text-sm font-medium border-b-2 transition ${
                      activeAuditType === type.id
                        ? 'border-blue-600 text-blue-600 bg-blue-500/10'
                        : `border-transparent ${theme.textTertiary} ${theme.bgHover}`
                    }`}
                  >
                    <type.icon className="w-4 h-4" />
                    {type.label}
                    <span
                      className={`ml-1 px-2 py-0.5 rounded-full text-xs ${
                        activeAuditType === type.id
                          ? 'bg-blue-500/20 text-blue-400'
                          : `${theme.bgTertiary} ${theme.textMuted}`
                      }`}
                    >
                      {type.count}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Filters & Search Bar */}
            <div className={`p-4 border-b ${theme.border} ${theme.bgTertiary}`}>
              <div className="flex items-center gap-4">
                {/* Search */}
                <div className="relative flex-1 max-w-md">
                  <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${theme.textMuted}`} />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search by ID, provider, NPI..."
                    className={`w-full pl-9 pr-4 py-2 ${theme.input} rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none`}
                  />
                </div>

                {/* Filter Toggle */}
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition ${
                    showFilters || hasActiveFilters
                      ? 'bg-blue-500/20 text-blue-400'
                      : `${theme.bgSecondary} border ${theme.border} ${theme.textSecondary} ${theme.bgHover}`
                  }`}
                >
                  <SlidersHorizontal className="w-4 h-4" />
                  Filters
                  {hasActiveFilters && (
                    <span className="w-5 h-5 bg-blue-600 text-white rounded-full text-xs flex items-center justify-center">
                      {Object.values(filters).filter((v) => v !== '').length + (searchQuery ? 1 : 0)}
                    </span>
                  )}
                </button>

                {/* Sort */}
                <div className="flex items-center gap-2">
                  <span className={`text-sm ${theme.textTertiary}`}>Sort by:</span>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className={`px-3 py-2 ${theme.input} rounded-lg text-sm`}
                  >
                    <option value="sla">SLA (Urgency)</option>
                    <option value="risk">Risk Score</option>
                    <option value="amount">Amount</option>
                    <option value="date">Service Date</option>
                  </select>
                  <button
                    onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                    className={`p-2 border ${theme.border} rounded-lg ${theme.bgHover}`}
                  >
                    <ArrowUpDown className={`w-4 h-4 ${theme.textTertiary}`} />
                  </button>
                </div>
              </div>

              {/* Expanded Filters */}
              {showFilters && (
                <div className={`mt-4 pt-4 border-t ${theme.border}`}>
                  <div className="flex items-end gap-4">
                    <div>
                      <label className={`block text-xs font-medium ${theme.textSecondary} mb-1`}>Region</label>
                      <select
                        value={filters.region}
                        onChange={(e) => setFilters({ ...filters, region: e.target.value })}
                        className={`px-3 py-2 ${theme.input} rounded-lg text-sm min-w-[140px]`}
                      >
                        <option value="">All Regions</option>
                        {regions.map((r) => (
                          <option key={r} value={r}>{r}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className={`block text-xs font-medium ${theme.textSecondary} mb-1`}>Vendor</label>
                      <select
                        value={filters.vendor}
                        onChange={(e) => setFilters({ ...filters, vendor: e.target.value })}
                        className={`px-3 py-2 ${theme.input} rounded-lg text-sm min-w-[140px]`}
                      >
                        <option value="">All Vendors</option>
                        {vendors.map((v) => (
                          <option key={v} value={v}>{v}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className={`block text-xs font-medium ${theme.textSecondary} mb-1`}>Risk Score</label>
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          placeholder="Min"
                          value={filters.riskScoreMin}
                          onChange={(e) => setFilters({ ...filters, riskScoreMin: e.target.value })}
                          className={`w-20 px-3 py-2 ${theme.input} rounded-lg text-sm`}
                        />
                        <span className={theme.textMuted}>-</span>
                        <input
                          type="number"
                          placeholder="Max"
                          value={filters.riskScoreMax}
                          onChange={(e) => setFilters({ ...filters, riskScoreMax: e.target.value })}
                          className={`w-20 px-3 py-2 ${theme.input} rounded-lg text-sm`}
                        />
                      </div>
                    </div>
                    <div>
                      <label className={`block text-xs font-medium ${theme.textSecondary} mb-1`}>Service Date</label>
                      <div className="flex items-center gap-2">
                        <input
                          type="date"
                          value={filters.dateFrom}
                          onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
                          className={`px-3 py-2 ${theme.input} rounded-lg text-sm`}
                        />
                        <span className={theme.textMuted}>-</span>
                        <input
                          type="date"
                          value={filters.dateTo}
                          onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
                          className={`px-3 py-2 ${theme.input} rounded-lg text-sm`}
                        />
                      </div>
                    </div>
                    {hasActiveFilters && (
                      <button
                        onClick={clearFilters}
                        className="inline-flex items-center gap-1 px-3 py-2 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-500/20 rounded-lg"
                      >
                        <X className="w-4 h-4" />
                        Clear
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Bulk Actions Bar */}
            {selectedItems.length > 0 && (
              <div className="p-3 bg-blue-50 border-b border-blue-200 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium text-blue-700">
                    {selectedItems.length} item{selectedItems.length > 1 ? 's' : ''} selected
                  </span>
                  <button
                    onClick={() => setSelectedItems([])}
                    className="text-sm text-blue-600 hover:text-blue-800"
                  >
                    Clear selection
                  </button>
                </div>
                <div className="flex items-center gap-2">
                  <button className="px-3 py-1.5 text-sm font-medium text-blue-700 hover:bg-blue-100 rounded-lg transition">
                    Assign to Me
                  </button>
                  <button className="px-3 py-1.5 text-sm font-medium text-blue-700 hover:bg-blue-100 rounded-lg transition">
                    Reassign
                  </button>
                  <button className="px-3 py-1.5 text-sm font-medium text-green-700 hover:bg-green-100 rounded-lg transition">
                    Bulk Approve
                  </button>
                  <button className="px-3 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg transition">
                    Export Selected
                  </button>
                </div>
              </div>
            )}

            {/* Table Header */}
            <div className={`grid grid-cols-12 gap-4 px-5 py-3 ${theme.bgTertiary} border-b ${theme.border} text-xs font-medium ${theme.textTertiary} uppercase tracking-wide`}>
              <div className="col-span-1 flex items-center">
                <button onClick={toggleSelectAll} className={`p-1 ${theme.bgHover} rounded`}>
                  {selectedItems.length === sortedItems.length && sortedItems.length > 0 ? (
                    <CheckSquare className="w-4 h-4 text-blue-600" />
                  ) : (
                    <Square className="w-4 h-4" />
                  )}
                </button>
              </div>
              <div className="col-span-3">Provider / Case ID</div>
              <div className="col-span-2">Type / Region</div>
              <div className="col-span-2 text-right">Amount</div>
              <div className="col-span-1 text-center">Risk</div>
              <div className="col-span-1 text-center">SLA</div>
              <div className="col-span-1">Assignee</div>
              <div className="col-span-1"></div>
            </div>

            {/* Queue Items */}
            <div className={`divide-y ${theme.borderLight}`}>
              {activeAuditType === 'Unresolved' ? (
                // Unresolved Items View
                unresolvedItems.map((item) => (
                  <div
                    key={item.id}
                    onClick={() => navigate(`/case/${item.id}`)}
                    className={`grid grid-cols-12 gap-4 px-5 py-4 ${theme.cardHover} transition cursor-pointer items-center`}
                  >
                    <div className="col-span-1">
                      <Square className={`w-4 h-4 ${theme.textMuted}`} />
                    </div>
                    <div className="col-span-3">
                      <p className={`font-medium ${theme.textPrimary}`}>{item.provider}</p>
                      <p className={`text-sm ${theme.textSecondary}`}>{item.id}</p>
                    </div>
                    <div className="col-span-2">
                      <div className="flex items-center gap-2">
                        <AuditTypeIcon type={item.auditType} className={`w-4 h-4 ${theme.textMuted}`} />
                        <span className={`text-sm ${theme.textSecondary}`}>{item.auditType}</span>
                      </div>
                      <p className={`text-xs ${theme.textMuted}`}>{item.region}</p>
                    </div>
                    <div className="col-span-2 text-right">
                      <p className={`font-medium ${theme.textPrimary}`}>
                        ${item.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                      </p>
                    </div>
                    <div className="col-span-1 text-center">
                      <span className="text-sm text-amber-600 dark:text-amber-400 font-medium">{item.daysOpen}d open</span>
                    </div>
                    <div className="col-span-2">
                      <span className="text-xs px-2 py-1 bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-400 rounded">
                        {item.status}
                      </span>
                    </div>
                    <div className="col-span-1 text-right">
                      <ChevronRight className={`w-5 h-5 ${theme.textMuted} inline`} />
                    </div>
                  </div>
                ))
              ) : (
                // Regular Queue Items
                sortedItems.map((item) => (
                  <div
                    key={item.id}
                    onClick={() => navigate(`/case/${item.id}`)}
                    className={`grid grid-cols-12 gap-4 px-5 py-4 ${theme.cardHover} transition cursor-pointer items-center group`}
                  >
                    <div className="col-span-1">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleSelectItem(item.id);
                        }}
                        className={`p-1 ${theme.bgHover} rounded`}
                      >
                        {selectedItems.includes(item.id) ? (
                          <CheckSquare className="w-4 h-4 text-blue-600" />
                        ) : (
                          <Square className={`w-4 h-4 ${theme.textMuted}`} />
                        )}
                      </button>
                    </div>
                    <div className="col-span-3">
                      <div className="flex items-center gap-2">
                        <p className={`font-medium ${theme.textPrimary}`}>{item.provider}</p>
                        <PriorityBadge priority={item.priority} />
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <p className={`text-sm ${theme.textSecondary}`}>{item.id}</p>
                        <span className={theme.textMuted}>•</span>
                        <p className={`text-sm ${theme.textMuted}`}>NPI: {item.npi}</p>
                      </div>
                      <div className="flex flex-wrap gap-1 mt-2">
                        {item.flagReasons.slice(0, 2).map((reason, idx) => (
                          <span
                            key={idx}
                            className={`inline-flex items-center gap-1 text-xs ${theme.bgTertiary} ${theme.textSecondary} px-2 py-0.5 rounded`}
                          >
                            <AlertTriangle className="w-3 h-3 text-amber-500" />
                            {reason}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="col-span-2">
                      <div className="flex items-center gap-2">
                        <AuditTypeIcon type={item.auditType} className={`w-4 h-4 ${theme.textMuted}`} />
                        <span className={`text-sm ${theme.textSecondary}`}>{item.auditType}</span>
                      </div>
                      <div className={`flex items-center gap-1 mt-1 text-xs ${theme.textMuted}`}>
                        <MapPin className="w-3 h-3" />
                        {item.region}
                      </div>
                      <div className={`flex items-center gap-1 mt-1 text-xs ${theme.textMuted}`}>
                        <Building className="w-3 h-3" />
                        {item.vendor}
                      </div>
                    </div>
                    <div className="col-span-2 text-right">
                      <p className={`text-lg font-semibold ${theme.textPrimary}`}>
                        ${item.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                      </p>
                      <p className={`text-xs ${theme.textMuted}`}>{item.serviceDate}</p>
                    </div>
                    <div className="col-span-1 flex justify-center">
                      <RiskScore score={item.riskScore} size="sm" />
                    </div>
                    <div className="col-span-1 flex justify-center">
                      <SLAIndicator hours={item.slaHours} status={item.slaStatus} />
                    </div>
                    <div className="col-span-1">
                      {item.assignee ? (
                        <div className="flex items-center gap-2">
                          <div className={`w-6 h-6 ${theme.bgTertiary} rounded-full flex items-center justify-center text-xs font-medium ${theme.textSecondary}`}>
                            {item.assignee.split(' ').map((n) => n[0]).join('')}
                          </div>
                          <span className={`text-xs ${theme.textSecondary} truncate max-w-[60px]`}>
                            {item.assignee.split(' ')[0]}
                          </span>
                        </div>
                      ) : (
                        <span className={`text-xs ${theme.textMuted} italic`}>Unassigned</span>
                      )}
                    </div>
                    <div className="col-span-1 text-right">
                      <ChevronRight className={`w-5 h-5 ${theme.textMuted} transition inline`} />
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Pagination */}
            <div className={`p-4 border-t ${theme.border} ${theme.bgTertiary} flex items-center justify-between`}>
              <div className={`text-sm ${theme.textSecondary}`}>
                Showing <span className="font-medium">{sortedItems.length}</span> of{' '}
                <span className="font-medium">{payments.length}</span> cases
              </div>
              <div className="flex items-center gap-2">
                <button className={`px-3 py-1.5 text-sm ${theme.textSecondary} ${theme.bgHover} rounded-lg disabled:opacity-50`} disabled>
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg">1</button>
                <button className={`px-3 py-1.5 text-sm ${theme.textSecondary} ${theme.bgHover} rounded-lg`}>2</button>
                <button className={`px-3 py-1.5 text-sm ${theme.textSecondary} ${theme.bgHover} rounded-lg`}>3</button>
                <span className={theme.textMuted}>...</span>
                <button className={`px-3 py-1.5 text-sm ${theme.textSecondary} ${theme.bgHover} rounded-lg`}>12</button>
                <button className={`px-3 py-1.5 text-sm ${theme.textSecondary} ${theme.bgHover} rounded-lg`}>
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-sm ${theme.textSecondary}`}>Per page:</span>
                <select className={`px-2 py-1 ${theme.input} rounded text-sm`}>
                  <option>25</option>
                  <option>50</option>
                  <option>100</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default CaseQueuePage;
